const DEFAULT_LATENCY_MS = 1500;
const LATENCY_SMOOTHING_FACTOR = 0.25;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 60_000;
const FAILURE_PENALTY_MS = 400;
const SUCCESS_BONUS_MS = 30;
const FAILURE_BACKOFF_GRACE = 2;
const STORAGE_KEY = 'bsvsdk_overlay_host_reputation_v3';
const LEGACY_STORAGE_KEY_V2 = 'bsvsdk_overlay_host_reputation_v2';
const LEGACY_STORAGE_KEY_V1 = 'bsvsdk_overlay_host_reputation_v1';
const STORAGE_DEBOUNCE_MS = 50;
const MAX_REPUTATION_ENTRIES = 256;
const REPUTATION_ENTRY_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export class HostReputationTracker {
    stats;
    store;
    saveTimer = null;
    constructor(store) {
        this.stats = new Map();
        this.store = store ?? this.getLocalStorageAdapter();
        this.loadFromStorage();
    }
    reset() {
        this.stats.clear();
        this.scheduleSave();
    }
    recordSuccess(host, latencyMs) {
        const entry = this.getOrCreate(host);
        const now = Date.now();
        const safeLatency = Number.isFinite(latencyMs) && latencyMs >= 0 ? latencyMs : DEFAULT_LATENCY_MS;
        if (entry.avgLatencyMs === null) {
            entry.avgLatencyMs = safeLatency;
        }
        else {
            entry.avgLatencyMs =
                (1 - LATENCY_SMOOTHING_FACTOR) * entry.avgLatencyMs + LATENCY_SMOOTHING_FACTOR * safeLatency;
        }
        entry.lastLatencyMs = safeLatency;
        entry.totalSuccesses += 1;
        entry.consecutiveFailures = 0;
        entry.backoffUntil = 0;
        entry.lastUpdatedAt = now;
        entry.lastError = undefined;
        this.scheduleSave();
    }
    recordFailure(host, reason) {
        const entry = this.getOrCreate(host);
        const now = Date.now();
        entry.totalFailures += 1;
        entry.consecutiveFailures += 1;
        let msg;
        if (typeof reason === 'string') {
            msg = reason;
        }
        else if (reason instanceof Error) {
            msg = reason.message;
        }
        else {
            msg = undefined;
        }
        const immediate = typeof msg === 'string' &&
            (msg.includes('ERR_NAME_NOT_RESOLVED') ||
                msg.includes('ENOTFOUND') ||
                msg.includes('getaddrinfo') ||
                msg.includes('Failed to fetch'));
        if (immediate && entry.consecutiveFailures < FAILURE_BACKOFF_GRACE + 1) {
            entry.consecutiveFailures = FAILURE_BACKOFF_GRACE + 1;
        }
        const penaltyLevel = Math.max(entry.consecutiveFailures - FAILURE_BACKOFF_GRACE, 0);
        if (penaltyLevel === 0) {
            entry.backoffUntil = 0;
        }
        else {
            const backoffDuration = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, penaltyLevel - 1));
            entry.backoffUntil = now + backoffDuration;
        }
        entry.lastUpdatedAt = now;
        if (typeof reason === 'string') {
            entry.lastError = reason;
        }
        else if (reason instanceof Error) {
            entry.lastError = reason.message;
        }
        else {
            entry.lastError = undefined;
        }
        this.scheduleSave();
    }
    rankHosts(hosts, now = Date.now()) {
        const seen = new Map();
        hosts.forEach((host, idx) => {
            if (typeof host !== 'string' || host.length === 0)
                return;
            if (!seen.has(host))
                seen.set(host, idx);
        });
        const orderedHosts = Array.from(seen.keys());
        const ranked = orderedHosts.map(host => {
            const entry = this.getOrCreate(host);
            return {
                ...entry,
                score: this.computeScore(entry, now),
                originalOrder: seen.get(host) ?? 0
            };
        });
        ranked.sort((a, b) => {
            const aInBackoff = a.backoffUntil > now;
            const bInBackoff = b.backoffUntil > now;
            if (aInBackoff !== bInBackoff)
                return aInBackoff ? 1 : -1;
            if (a.score !== b.score)
                return a.score - b.score;
            if (a.totalSuccesses !== b.totalSuccesses)
                return b.totalSuccesses - a.totalSuccesses;
            return a.originalOrder - b.originalOrder;
        });
        return ranked.map(({ originalOrder: _originalOrder, ...rest }) => rest);
    }
    snapshot(host) {
        const entry = this.stats.get(host);
        return entry == null ? undefined : { ...entry };
    }
    /** Flushes a pending debounced persistence write immediately. */
    flush() {
        if (this.saveTimer !== null) {
            clearTimeout(this.saveTimer);
            this.saveTimer = null;
        }
        this.saveToStorage();
    }
    getStorage() {
        try {
            const g = typeof globalThis === 'object' ? globalThis : undefined;
            if (g?.localStorage == null)
                return undefined;
            return g.localStorage;
        }
        catch {
            return undefined;
        }
    }
    getLocalStorageAdapter() {
        const s = this.getStorage();
        if (s == null)
            return undefined;
        return {
            get: (key) => {
                try {
                    return s.getItem(key);
                }
                catch {
                    return null;
                }
            },
            set: (key, value) => {
                try {
                    s.setItem(key, value);
                }
                catch { }
            }
        };
    }
    readStoredReputation(store) {
        for (const key of [STORAGE_KEY, LEGACY_STORAGE_KEY_V2, LEGACY_STORAGE_KEY_V1]) {
            const raw = store.get(key);
            if (typeof raw === 'string' && raw.length > 0)
                return raw;
        }
        return undefined;
    }
    parseStoredEntry(key, value) {
        if (value == null || typeof value !== 'object')
            return undefined;
        const stored = value;
        return {
            host: String(stored.host ?? key),
            totalSuccesses: Number(stored.totalSuccesses ?? 0),
            totalFailures: Number(stored.totalFailures ?? 0),
            consecutiveFailures: Number(stored.consecutiveFailures ?? 0),
            avgLatencyMs: stored.avgLatencyMs == null ? null : Number(stored.avgLatencyMs),
            lastLatencyMs: stored.lastLatencyMs == null ? null : Number(stored.lastLatencyMs),
            backoffUntil: Number(stored.backoffUntil ?? 0),
            lastUpdatedAt: Number(stored.lastUpdatedAt ?? 0),
            lastError: typeof stored.lastError === 'string' ? stored.lastError : undefined
        };
    }
    loadFromStorage() {
        const s = this.store;
        if (s == null)
            return;
        try {
            const raw = this.readStoredReputation(s);
            if (raw === undefined)
                return;
            const data = JSON.parse(raw);
            if (typeof data !== 'object' || data === null)
                return;
            this.stats.clear();
            for (const k of Object.keys(data)) {
                const entry = this.parseStoredEntry(k, data[k]);
                if (entry !== undefined)
                    this.stats.set(entry.host, entry);
            }
            this.prune(Date.now());
        }
        catch { }
    }
    scheduleSave() {
        if (this.store == null || this.saveTimer !== null)
            return;
        this.saveTimer = setTimeout(() => {
            this.saveTimer = null;
            this.saveToStorage();
        }, STORAGE_DEBOUNCE_MS);
        const timer = this.saveTimer;
        timer.unref?.();
    }
    saveToStorage() {
        const s = this.store;
        if (s == null)
            return;
        try {
            this.prune(Date.now());
            const obj = {};
            for (const [host, entry] of this.stats.entries()) {
                obj[host] = entry;
            }
            s.set(STORAGE_KEY, JSON.stringify(obj));
        }
        catch { }
    }
    computeScore(entry, now) {
        const latency = entry.avgLatencyMs ?? DEFAULT_LATENCY_MS;
        const failurePenalty = entry.consecutiveFailures * FAILURE_PENALTY_MS;
        const successBonus = Math.min(entry.totalSuccesses * SUCCESS_BONUS_MS, latency / 2);
        const backoffPenalty = entry.backoffUntil > now ? entry.backoffUntil - now : 0;
        return latency + failurePenalty + backoffPenalty - successBonus;
    }
    getOrCreate(host) {
        let entry = this.stats.get(host);
        if (entry == null) {
            this.prune(Date.now());
            if (this.stats.size >= MAX_REPUTATION_ENTRIES)
                this.evictOldestEntry();
            entry = {
                host,
                totalSuccesses: 0,
                totalFailures: 0,
                consecutiveFailures: 0,
                avgLatencyMs: null,
                lastLatencyMs: null,
                backoffUntil: 0,
                lastUpdatedAt: 0
            };
            this.stats.set(host, entry);
        }
        return entry;
    }
    prune(now) {
        for (const [host, entry] of this.stats) {
            if (entry.lastUpdatedAt > 0 && now - entry.lastUpdatedAt > REPUTATION_ENTRY_TTL_MS) {
                this.stats.delete(host);
            }
        }
        while (this.stats.size > MAX_REPUTATION_ENTRIES)
            this.evictOldestEntry();
    }
    evictOldestEntry() {
        let oldestHost;
        let oldestUpdatedAt = Number.POSITIVE_INFINITY;
        for (const [host, entry] of this.stats) {
            if (entry.lastUpdatedAt < oldestUpdatedAt) {
                oldestHost = host;
                oldestUpdatedAt = entry.lastUpdatedAt;
            }
        }
        if (oldestHost !== undefined)
            this.stats.delete(oldestHost);
    }
}
const globalTracker = new HostReputationTracker();
export const getOverlayHostReputationTracker = () => globalTracker;
//# sourceMappingURL=HostReputationTracker.js.map